import Konva from 'konva';
import { Node } from 'konva/lib/Node';

import { EMagicPenShape } from '@src/types';

import { ERASER_INTERPOLATION_STEP_PX } from '../magicPen.constants';

export const createTextInput = (
  textNode: Konva.Text,
  initialText: string,
  originalColor: string
): HTMLTextAreaElement => {
  const textEditorInput = document.createElement('textarea');

  // Get font properties from Konva text node to match exactly
  const fontSize = textNode.fontSize();

  textEditorInput.value = initialText;
  textEditorInput.style.position = 'absolute';
  textEditorInput.style.top = '0';
  textEditorInput.style.left = '0';
  textEditorInput.style.width = '100%';
  textEditorInput.style.height = '100%';
  textEditorInput.style.fontSize = fontSize + 'px';
  textEditorInput.style.border = 'none';
  textEditorInput.style.padding = (textNode.padding() || 0) + 'px';
  textEditorInput.style.margin = '0px';
  textEditorInput.style.overflow = 'hidden';
  textEditorInput.style.overflowY = 'auto'; // Allow scrolling but hide scrollbar
  (textEditorInput.style as any).scrollbarWidth = 'none'; // Firefox
  (textEditorInput.style as any).msOverflowStyle = 'none'; // IE/Edge
  textEditorInput.style.whiteSpace = 'pre-wrap'; // Enable text wrapping
  textEditorInput.style.wordBreak = 'break-word'; // Break long words if needed
  textEditorInput.wrap = 'soft'; // Enable soft wrapping
  textEditorInput.style.background = 'transparent';
  textEditorInput.style.outline = 'none';
  textEditorInput.style.resize = 'none';
  textEditorInput.style.lineHeight = textNode.lineHeight().toString();
  textEditorInput.style.fontFamily = textNode.fontFamily() || 'Arial';
  textEditorInput.style.color = originalColor;
  // Default caret color (typing cursor) should be black
  (textEditorInput.style as any).caretColor = '#000000';
  // Scope styling using a class to avoid global side effects
  textEditorInput.classList.add('konva-text-editor');
  // Match Konva's wrapping behavior: width applies to content area, padding is extra
  textEditorInput.style.boxSizing = 'content-box';
  textEditorInput.style.cursor = 'text';
  textEditorInput.style.fontStyle = textNode.fontStyle() || 'normal';
  textEditorInput.style.fontVariant = textNode.fontVariant() || 'normal';
  // Improve touchpad interaction support
  textEditorInput.style.touchAction = 'manipulation';
  textEditorInput.style.userSelect = 'text';

  // Add style rule to hide webkit scrollbar
  const style = document.createElement('style');
  style.textContent = `
    .konva-text-editor::-webkit-scrollbar {
      display: none;
    }
  `;
  if (!document.head.querySelector('style[data-hide-scrollbar-kte]')) {
    style.setAttribute('data-hide-scrollbar-kte', 'true');
    document.head.appendChild(style);
  }

  return textEditorInput;
};

export const createTextEditorWrapper = (
  textNode: Konva.Text,
  areaPosition: { x: number; y: number },
  stageContainer?: HTMLDivElement
): HTMLDivElement => {
  const textEditorWrapper = document.createElement('div');
  document.body.appendChild(textEditorWrapper);

  // Calculate default width to fit approximately 15 characters
  const fontSize = textNode.fontSize();
  const DEFAULT_CHAR_COUNT = 15;
  const defaultWidth = fontSize * DEFAULT_CHAR_COUNT * 0.6; // Approximate character width

  // Get the actual text dimensions from the text node
  const nodeWrapWidth = textNode.width() || 0; // Konva wrapping width if set
  const textHeight = textNode.height() || fontSize;

  // Start with default width for new nodes; for existing nodes, use current Konva width
  const wrapperWidth = nodeWrapWidth > 0 ? nodeWrapWidth : defaultWidth;
  const wrapperHeight = Math.max(textHeight, fontSize + 20); // Add padding for better initial appearance

  let finalLeft = areaPosition.x;
  let finalTop = areaPosition.y;
  let maxWidth: number | undefined;
  let maxHeight: number | undefined;

  // Constrain initial position to canvas bounds
  if (stageContainer) {
    const stageRect = stageContainer.getBoundingClientRect();
    const topReduction = fontSize * 0.08;
    finalLeft = Math.max(stageRect.left, Math.min(areaPosition.x, stageRect.right - wrapperWidth));
    finalTop = Math.max(stageRect.top + topReduction, Math.min(areaPosition.y, stageRect.bottom - wrapperHeight));

    // Calculate maximum dimensions based on canvas bounds
    maxWidth = stageRect.right - finalLeft;
    maxHeight = stageRect.bottom - finalTop;
  }

  // Style the wrapper
  textEditorWrapper.style.position = 'absolute';
  textEditorWrapper.style.top = finalTop + 'px';
  textEditorWrapper.style.left = finalLeft + 'px';
  textEditorWrapper.style.width = Math.max(1, wrapperWidth) + 'px';
  textEditorWrapper.style.height = Math.max(1, wrapperHeight) + 'px';
  if (maxWidth !== undefined) {
    textEditorWrapper.style.maxWidth = maxWidth + 'px';
  }
  if (maxHeight !== undefined) {
    textEditorWrapper.style.maxHeight = maxHeight + 'px';
  }
  // Use outline instead of border so editor content width matches Konva content width
  textEditorWrapper.style.outline = '2px solid #1890ff';
  textEditorWrapper.style.boxSizing = 'border-box';
  textEditorWrapper.style.zIndex = '1000';
  textEditorWrapper.style.cursor = 'move';
  textEditorWrapper.style.background = 'transparent';
  // Improve touchpad interaction support
  textEditorWrapper.style.touchAction = 'manipulation';
  textEditorWrapper.style.userSelect = 'text';

  // Apply rotation and scale
  const rotation = textNode.rotation();
  let transform = '';
  if (rotation) {
    transform += 'rotateZ(' + rotation + 'deg)';
  }
  const scale = textNode.getAbsoluteScale().x;
  transform += ' scale(' + scale + ')';
  textEditorWrapper.style.transform = transform;

  return textEditorWrapper;
};

const textEditorRegistry: Record<string, HTMLDivElement> = {};

export const registerTextEditor = (id: string, wrapper: HTMLDivElement): void => {
  textEditorRegistry[id] = wrapper;
};

export const unregisterTextEditor = (id: string): void => {
  delete textEditorRegistry[id];
};

const removeWrapper = (wrapper?: HTMLDivElement): void => {
  if (wrapper?.parentNode) {
    wrapper.parentNode.removeChild(wrapper);
  }
};

export const destroyTextEditorsByIds = (ids: string[]): void => {
  ids.forEach((id) => {
    removeWrapper(textEditorRegistry[id]);
    delete textEditorRegistry[id];
  });
};

export const destroyAllTextEditors = (): void => {
  Object.keys(textEditorRegistry).forEach((id) => {
    removeWrapper(textEditorRegistry[id]);
    delete textEditorRegistry[id];
  });
};

// Determine which color property to update based on object type
export const getColorPropertyForShape = (
  type: EMagicPenShape,
  color: string
): { stroke?: string; fill?: string } | null => {
  const shapeTypes = [EMagicPenShape.RECTANGLE, EMagicPenShape.CIRCLE, EMagicPenShape.ARROW, EMagicPenShape.LINE];

  if (shapeTypes.includes(type)) {
    return { stroke: color };
  }

  if (type === EMagicPenShape.TEXT_BOX) {
    return { fill: color };
  }

  return null;
};

export const getObjectIdFromNode = (node: Node): string => {
  let cur: Node | null = node;
  while (cur && cur.getType() !== 'Stage' && cur.getType() !== 'Layer') {
    const id = cur.id();
    if (id) return id;
    cur = cur.getParent() as Node | null;
  }

  return '';
};

export const getInterpolatedPoints = (
  current: { x: number; y: number },
  last: { x: number; y: number } | null,
  step = ERASER_INTERPOLATION_STEP_PX
) => {
  if (!last) return [current];

  const { x: x1, y: y1 } = last;
  const { x: x2, y: y2 } = current;

  const distance = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
  const steps = Math.ceil(distance / step);

  const points = [{ x: x2, y: y2 }];

  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    points.push({
      x: x1 + (x2 - x1) * t,
      y: y1 + (y2 - y1) * t,
    });
  }

  return points;
};

// Timeout array to store TTL timeout IDs for disappearing ink objects (it can be reused for any object with TTL)
const ttlTimeouts: NodeJS.Timeout[] = [];

// Remove a timeout ID from the tracking array
export const removeTtlTimeout = (timeoutId: NodeJS.Timeout): void => {
  const index = ttlTimeouts.indexOf(timeoutId);
  if (index > -1) {
    ttlTimeouts.splice(index, 1);
  }
};

// Cancel all pending TTL timeouts and clear the array in case they are already deleted, to avoid redundant network calls
export const cancelAllTtlTimeouts = (): void => {
  ttlTimeouts.forEach((timeoutId) => {
    clearTimeout(timeoutId);
  });
  ttlTimeouts.length = 0;
};

export const ttlTimeoutsArray = ttlTimeouts;
